import {
  buttonVariants,
  Card,
  CardContent,
  EmptyState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@forge/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Package } from "lucide-react";
import { CopyButton } from "../../components/copy-button";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { formatDelta, MOVEMENT_KIND_LABEL } from "../../lib/stock";

export const Route = createFileRoute("/_app/stock_/$id")({
  component: SkuDetailPage,
});

function SkuDetailPage() {
  const { id } = Route.useParams();
  const detail = useQuery({
    queryKey: ["skus", id],
    queryFn: async () => unwrap(await api.skus({ id }).get()),
  });

  if (detail.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-5 text-ink-muted" />
      </div>
    );
  }

  const data = detail.data && "sku" in detail.data ? detail.data : null;
  if (!data) {
    return (
      <Card>
        <EmptyState
          icon={<Package />}
          title="SKU não encontrado"
          description="Volte ao estoque e escolha um SKU da lista."
          action={
            <Link
              to="/stock"
              className={buttonVariants({ variant: "secondary" })}
            >
              Voltar ao estoque
            </Link>
          }
        />
      </Card>
    );
  }

  const { sku, movements } = data;
  const warehouseHint = sku.stocks
    .map((row) => `${row.warehouseName}: ${row.quantity}`)
    .join(" · ");

  return (
    <>
      <PageHeader
        parent={
          <Link
            to="/stock"
            className="text-2xs font-medium text-ink-muted transition-colors duration-100 hover:text-ink"
          >
            Estoque
          </Link>
        }
        title={sku.code}
        description={sku.title}
        actions={<CopyButton value={sku.code} label="Copiar SKU" />}
      />

      <div className="flex flex-col gap-3">
        <Card className="animate-rise">
          <CardContent className="flex flex-col gap-1 p-4">
            <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
              Saldo
            </p>
            <p className="font-mono text-2xl font-medium tracking-tight text-ink">
              {sku.totalQuantity}
              <span className="ml-1.5 text-[0.8125rem] font-normal text-ink-muted">
                un.
              </span>
            </p>
            <p className="text-[0.8125rem] text-ink-secondary">
              {warehouseHint || "Sem depósito cadastrado"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <p className="border-b border-line px-4 py-2 text-2xs font-medium text-ink-muted">
            Por que o saldo é {sku.totalQuantity}?
          </p>
          {movements.length === 0 ? (
            <EmptyState
              icon={<Package />}
              title="Nenhum movimento ainda"
              description="Vendas, ajustes, entradas do ERP e recebimentos de compra aparecem aqui."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Quando</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-right">Movimento</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-2xs text-ink-muted">
                      {formatDateTime(row.createdAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex min-w-0 flex-col">
                        <span className="text-[0.8125rem] text-ink">
                          {MOVEMENT_KIND_LABEL[row.kind] ?? row.kind}
                          {row.reason ? ` · ${row.reason}` : ""}
                        </span>
                        <span className="text-2xs text-ink-muted">
                          {row.warehouseName}
                          {row.actorName ? ` · ${row.actorName}` : ""}
                          {row.refId ? ` · ${row.refId}` : ""}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell
                      className={
                        row.delta < 0
                          ? "text-right font-mono text-danger"
                          : "text-right font-mono text-ink"
                      }
                    >
                      {formatDelta(row.delta)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-ink">
                      {row.balanceAfter}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}
